import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { noop, isEqual, floor, size, get, filter, includes } from 'lodash'
import cn from 'classnames'

import Table from '@scoir/components-table/dist/Table'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core'

import { actions as snactions } from 'store/snackbar'
import api from 'common/api'
import { getUserType, getUserTypeDisplay } from 'components/SegmentSelector/UserTypeSelector'
import {
  getIsOneTimeMessage,
  getIsUsingManualStudentSelection,
  getIsUsingMyMatches,
} from 'components/SegmentSelector/segment-service'
import { COLUMN_CONFIG } from '../table-helpers'
import ScheduledDate from './ScheduledDate'
import DateCreated from './DateCreated'
import Stat from './Stat'
import Message from './Message'
import Priority from './Priority'
import Actions from './Actions'
import './index.scss'
import { withTheme } from '@material-ui/core/styles'
import { Box } from '@material-ui/core'

const getAudienceDisplay = (segment, trigger) => {
  const isOneTimeMessage = getIsOneTimeMessage(trigger)
  if (!isOneTimeMessage) {
    return getUserTypeDisplay(segment)
  }

  const isUsingManualStudentSelection = getIsUsingManualStudentSelection(segment)
  const isUsingMyMatches = getIsUsingMyMatches(segment)
  const audience = isUsingManualStudentSelection ? 'IDs' : isUsingMyMatches ? 'Matches' : 'Followers'
  const userType = getUserType(segment)
  return `${userType} ${audience}`
}

const DeleteConfirmationModal = ({ open, onClose, onDelete }) => {
  const [deleted, setDeleted] = useState(false)
  const handleDelete = useCallback(() => {
    setDeleted(true)
    onDelete()
  }, [onDelete])
  return (
    <Dialog
      open={open}
      onClose={onClose}
      onExited={() => setDeleted(false)}
      aria-labelledby="confirm-delete-title"
    >
      <DialogTitle id="confirm-delete-title">Confirm message deletion</DialogTitle>
      <DialogContent>Are you sure you want to delete this message?</DialogContent>
      <DialogActions>
        <Button
          variant="text"
          color="primary"
          onClick={onClose}
        >
          No
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={deleted}
          onClick={handleDelete}
        >
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  )
}
DeleteConfirmationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
}

export class MessageTable extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    messages: PropTypes.array.isRequired,
    onChange: PropTypes.func,
    scid: PropTypes.number.isRequired,
    addSnack: PropTypes.func.isRequired,
    theme: PropTypes.object.isRequired,
    config: PropTypes.shape({
      omitColumns: PropTypes.array,
      columnDefs: PropTypes.object,
    }),
  }
  static defaultProps = {
    config: {},
  }
  state = {
    editMessageUpdated: undefined,
    deleteId: undefined,
    sortDir: 'desc',
    sortKey: 'ScheduledDate',
  }
  deleteMessage(id) {
    const { scid, addSnack, onChange } = this.props
    return api
      .delete(`/api/college/${scid}/message/${id}`)
      .then(() => onChange())
      .catch(() => addSnack('An error occurred while deleting this message.'))
  }
  deleteRecord(id) {
    this.deleteMessage(id).finally(() => this.setState({ deleteId: undefined }))
  }

  updateEnabledState(record, enabled) {
    const { scid, addSnack, onChange } = this.props
    return api
      .patch(`/api/college/${scid}/message/${record.Id}`, record, { ...record, Disabled: !enabled })
      .then(() => onChange())
      .catch(() => addSnack('An error occurred while updating this message.'))
  }
  setEnabledState(record, active) {
    this.updateEnabledState(record, active)
  }

  renderCell(content) {
    return (record) => <Box color={record.Disabled && this.props.theme.palette.text.disabled}>{content(record)}</Box>
  }

  getColumnDefs() {
    const { config, messages } = this.props
    const { omitColumns = [] } = config
    const showColumn = (name) => !includes(omitColumns, name)
    const columnDefs = filter([
      showColumn(COLUMN_CONFIG.priorityColumn) && {
        headerText: 'Priority',
        renderCell: this.renderCell((record) => (
          <Priority
            message={record}
            onChange={() => this.props.onChange()}
            isFirst={isEqual(record.Priority, 1)}
            isLast={isEqual(record.Priority, size(messages))}
          />
        )),
        ...get(config, `columnDefs.${COLUMN_CONFIG.priorityColumn}`, {}),
      },
      showColumn(COLUMN_CONFIG.dateCreatedColumn) && {
        headerText: 'Date Created',
        sortKey: 'CreatedDate',
        renderCell: this.renderCell((record) => <DateCreated record={record} />),
        ...get(config, `columnDefs.${COLUMN_CONFIG.dateCreatedColumn}`, {}),
      },
      showColumn(COLUMN_CONFIG.scheduledDateColumn) && {
        sortKey: 'ScheduledDate',
        renderCell: this.renderCell((record) => <ScheduledDate record={record} />),
        ...get(config, `columnDefs.${COLUMN_CONFIG.scheduledDateColumn}`, {}),
      },
      showColumn(COLUMN_CONFIG.messageColumn) && {
        headerText: 'Message',
        renderCell: this.renderCell((record) => <Message outreachItem={record} />),
        ...get(config, `columnDefs.${COLUMN_CONFIG.messageColumn}`, {}),
      },
      showColumn(COLUMN_CONFIG.audienceColumn) && {
        headerText: 'Audience',
        renderCell: this.renderCell((record) => getAudienceDisplay(record.Segment, record.Trigger)),
        ...get(config, `columnDefs.${COLUMN_CONFIG.audienceColumn}`, {}),
      },
      showColumn(COLUMN_CONFIG.totalSentColumn) && {
        headerText: 'Total Sent',
        sortKey: 'TotalSent',
        renderCell: this.renderCell((record) => <Stat>{record.TotalSent}</Stat>),
        ...get(config, `columnDefs.${COLUMN_CONFIG.totalSentColumn}`, {}),
      },
      showColumn(COLUMN_CONFIG.opensColumn) && {
        headerText: 'Opens',
        sortKey: 'TotalOpened',
        renderCell: this.renderCell((record) => <Stat>{record.TotalOpened}</Stat>),
        ...get(config, `columnDefs.${COLUMN_CONFIG.opensColumn}`, {}),
      },
      showColumn(COLUMN_CONFIG.openRateColumn) && {
        headerText: 'Open Rate',
        sortKey: 'OpenRate',
        renderCell: this.renderCell((record) => (
          <Stat>{floor((record.TotalOpened * 100) / Math.max(record.TotalSent, 1), 2) + '%'}</Stat>
        )),
        ...get(config, `columnDefs.${COLUMN_CONFIG.openRateColumn}`, {}),
      },
      showColumn(COLUMN_CONFIG.clicksColumn) && {
        headerText: 'Clicks',
        sortKey: 'TotalClicked',
        renderCell: this.renderCell((record) => <Stat>{record.TotalClicked}</Stat>),
        ...get(config, `columnDefs.${COLUMN_CONFIG.clicksColumn}`, {}),
      },
      {
        headerText: 'Actions',
        renderCell: this.renderCell((record) => (
          <Actions
            record={record}
            onDelete={() => this.setState({ deleteId: record.Id })}
            onEnable={() => this.setEnabledState(record, true)}
            onDisable={() => this.setEnabledState(record, false)}
          />
        )),
      },
    ])
    return columnDefs
  }

  onSortChange = (targetSortKey, sortDir) => {
    const newSortDir = sortDir === undefined ? 'asc' : sortDir
    this.setState({
      sortKey: targetSortKey,
      sortDir: newSortDir,
    })
  }

  sortMessages = (messages) => {
    return messages.sort((a, b) => {
      let firstValue = a[this.state.sortKey]
      let secondValue = b[this.state.sortKey]

      // if sorting by scheduled date, use sent date if scheduled date is undefined
      if (this.state.sortKey === 'ScheduledDate') {
        if (firstValue === undefined) {
          firstValue = a['SentDate']
        }
        if (secondValue === undefined) {
          secondValue = b['SentDate']
        }
      } else if (this.state.sortKey === 'OpenRate') {
        firstValue = floor((a['TotalOpened'] * 100) / Math.max(a['TotalSent'], 1), 2)
        secondValue = floor((b['TotalOpened'] * 100) / Math.max(b['TotalSent'], 1), 2)
      }

      if (this.state.sortDir === 'asc') {
        return firstValue > secondValue ? 1 : -1
      } else {
        return firstValue < secondValue ? 1 : -1
      }
    })
  }

  render() {
    const { className, messages } = this.props
    const columnDefs = this.getColumnDefs()
    const handleClose = () => this.setState({ deleteId: undefined })

    return (
      <div className={cn('outreach-message-table', className)}>
        <Table
          trackHistory={false}
          total={1}
          records={this.sortMessages(messages)}
          keyedBy="Id"
          canChangePageSize={false}
          paginated={false}
          onTableChange={noop}
          columnDefs={columnDefs}
          sortDir={this.state.sortDir}
          sortKey={this.state.sortKey}
          onSortChange={this.onSortChange}
        />
        <DeleteConfirmationModal
          open={!!this.state.deleteId}
          onClose={handleClose}
          onDelete={() => this.deleteRecord(this.state.deleteId)}
        />
      </div>
    )
  }
}
export default connect(
  (store) => ({
    scid: get(store.user, 'record.Scid'),
  }),
  {
    addSnack: snactions.addSnackbarItem,
  },
)(withTheme(MessageTable))
